import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
  SyntheticEvent,
} from "react";
import { useLayoutEffect } from "react";
import SwapVertIcon from "@mui/icons-material/SwapVert";

import { computePosition, flip, shift } from "@floating-ui/react";

import {
  DEFAULT_NEW_COLUMN_CLASSES,
  getComponentType,
  inClass,
} from "../../../../utils/Helpers";
import {
  MoreHorizOutlined,
} from "@mui/icons-material";

import Confirmation from "../Confirmation/Confirmation";
import CreateTemplateModal from "../Modals/CreateTemplateModal";
import BuilderAlertModal from "../../Modal/BuilderAlertModal";
import { setEditableComponentId } from "../../../../store/reducers/textEditor/textEditorActions";
import SelectElementType from "./SelectElementType/SelectElementType";
import SelectTemplate from "./SelectTemplate/SelectTemplate";
import WidthWideOutlinedIcon from "@mui/icons-material/WidthWideOutlined";
import AddIcon from "@mui/icons-material/Add";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StructureAPI from "../../../../store/reducers/builderReducers/structure/structureApi";
import {
  updateStructureComponent,
  addNewComponent,
  deleteComponentById,
  updateComponentStructureOrder,
  updateStructureWebpageComponentsOrder,
} from "../../../../store/reducers/builderReducers/structure/structureActions";
import { useAppSelector } from "../../../../store/hooks";
import { AllComponents } from "../../../_default/interfaces/base";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Dropdown from "react-overlays/Dropdown";
import { createPortal } from "react-dom";

import Spinner from "../Spinner/Spinner";
import TemplateAPI, {
  isTemplateDeleteConflictError,
  parseTemplateDeleteConflictMessage,
} from "../../../../store/reducers/builderReducers/templates/templateApi";
import { findTemplateForComponent } from "../../../../store/reducers/builderReducers/templates/templateHelpers";
import TextEditorComponent from "./TextEditorComponent/TextEditorComponent";
import {
  useBuilderExclusiveOverflowMenu,
  EDITOR_FLOATING_DROPDOWN_OVERFLOW_ID,
  EDITOR_ADD_ELEMENT_MORE_OVERFLOW_ID,
  broadcastExclusiveOverflowMenuActive,
  subscribeExclusiveOverflowMenuActive,
  getExclusiveOverflowMenuActiveId,
} from "../useBuilderExclusiveOverflowMenu";
import SelectEditorMenu from "./SelectEditorMenu/SelectEditorMenu";
import BottomMenu from "./BottomMenu/BottomMenu";

// Drag and drop

import { handleMouseDown } from "./DragNDrop/handleMouseDown";
import { handleMouseUp } from "./DragNDrop/handleMouseUp";
import { handleDragStart } from "./DragNDrop/handleDragStart";
import { handleDragEnd } from "./DragNDrop/handleDragEnd";
import { handleDragOverPos } from "./DragNDrop/handleDragOverPos";
import { handleDragLeavePos } from "./DragNDrop/handleDragLeavePos";
import {
  isColumnReorderLayoutParent,
  resolveColumnReorderLayoutParent,
} from "./DragNDrop/columnReorderLayoutParent";
import type { Editor as EditorState } from "../../../../store/reducers/builderReducers/editor/editorTypes";
import {
  getBuilderComponentRootElement,
  getComponentDomId,
  getEditorParentComponentMapKey,
  resolveEditorComponentStructureKey,
} from "../../../../store/reducers/builderReducers/editor/editorSelectionHelpers";
import { getLegacyWebpageIdForStructure } from "../../../../store/reducers/builderReducers/pages/pageIdentity";
import type { Page } from "../../../../store/reducers/builderReducers/pages/pagesTypes";
import {
  editorTemplateMatchesComponent,
  getComponentFromStructureMap,
  getComponentIdentityKey,
} from "../../../_default/utilities/componentIdentity";
import {
  isStructureReorderAllowed,
  type BuilderDnDReorderGate,
} from "../../../../store/reducers/builderReducers/structure/structureReorderPlan";

/** Parent key for reorder APIs when map uses UUID or legacy numeric ids. */
function resolveStructureParentKeyForOrder(
  parentComp: AllComponents | undefined,
  fallbackParentId: number | string | null
): string | number {
  if (parentComp) {
    const k = getComponentIdentityKey(parentComp);
    if (k) return k;
  }
  if (fallbackParentId != null) return fallbackParentId;
  return 0;
}

function resolveOrderParentKey(
  parent: AllComponents | undefined,
  child: AllComponents
): string | number {
  if (child.type === "image") {
    if (child.parent_id != null) return child.parent_id;
    const u = (child as { parent_uuid?: string | null }).parent_uuid?.trim();
    if (u) return u;
  }
  return resolveStructureParentKeyForOrder(parent, child.parent_id);
}

function shouldDispatchStructureReorder(
  components: Record<string, AllComponents>,
  args: {
    parent_id: string | number;
    draggedComponentId: string | null;
    destinationId: string | null;
    position: string | null;
    section: boolean;
  }
): boolean {
  const { draggedComponentId, destinationId } = args;
  if (draggedComponentId == null || destinationId == null) {
    return false;
  }
  return isStructureReorderAllowed(components, {
    parent_id: args.parent_id,
    draggedComponentId,
    destinationId,
    position: args.position ?? "middle",
    section: args.section,
  });
}

/**
 * Parent keys for {@link StructureAPI.addComponent} when inserting a sibling.
 * If the selection has no parent (page root), return {} — do **not** pass
 * `parent_id: null`, or addComponent will fall back to the selected row as parent.
 */
/**
 * 1-based sort_order for {@link StructureAPI.addComponent} when appending a new
 * child. Passing the parent's own sibling sort_order + 1 inserts at that index
 * among children (e.g. row with sort_order 1 → 2 → second column).
 */
function appendChildSortOrder(parent: AllComponents): number {
  return (parent.childWebpageComponentRelations?.length ?? 0) + 1;
}

function siblingAddParentPayload(
  component: AllComponents,
  parentComponent: AllComponents | undefined | null
): Record<string, unknown> {
  const parentUuid =
    (component as { parent_uuid?: string | null }).parent_uuid?.trim()
      ? (component as { parent_uuid?: string | null }).parent_uuid
      : parentComponent?.uuid?.trim() || null;
  const parentId = component.parent_id;
  const hasParent =
    parentId != null ||
    (typeof parentUuid === "string" && parentUuid.trim() !== "");
  if (!hasParent) {
    return {};
  }
  const out: Record<string, unknown> = {};
  if (parentId != null) {
    out.parent_id = parentId;
  }
  if (typeof parentUuid === "string" && parentUuid.trim() !== "") {
    out.parent_uuid = parentUuid;
  }
  return out;
}

/**
 * 1-based `sort_order` for {@link StructureAPI.addComponent} so the new sibling is
 * inserted immediately to the right of `sibling`. Matches {@link SelectElementType}
 * (`selectedIndexInParent + 2`); using `sibling.sort_order + 1` is wrong when
 * `sort_order` is stale vs. {@link AllComponents.childWebpageComponentRelations}.
 */
function sortOrderInsertSiblingAfter(
  sibling: AllComponents,
  parent: AllComponents | null | undefined,
  structureComponents: Record<string, AllComponents>
): number {
  if (!parent) {
    return Number(sibling.sort_order ?? 0) + 1;
  }
  const siblingKey = getComponentIdentityKey(sibling);
  const idx = (parent.childWebpageComponentRelations ?? []).findIndex(
    (relation) => {
      const relationChildRef =
        relation.child_uuid?.trim() || relation.child_id;
      const childComp = getComponentFromStructureMap(
        structureComponents,
        relationChildRef
      );
      return (
        childComp != null &&
        getComponentIdentityKey(childComp) === siblingKey
      );
    }
  );
  if (idx >= 0) {
    return idx + 2;
  }
  return Number(sibling.sort_order ?? 0) + 1;
}

/**
 * When selection is nested under `host`, returns the immediate child of `host` on the path to `selected`.
 * If `selected` is `host`, returns null (caller should use {@link appendChildSortOrder}).
 */
function getDirectChildUnderHost(
  host: AllComponents,
  selected: AllComponents,
  structureComponents: Record<string, AllComponents>
): AllComponents | null {
  const hostKey = getComponentIdentityKey(host);
  const selectedKey = getComponentIdentityKey(selected);
  if (!hostKey || !selectedKey) {
    return null;
  }
  if (selectedKey === hostKey) {
    return null;
  }
  let current: AllComponents | undefined = selected;
  const visited = new Set<string>();
  while (current) {
    const ck = getComponentIdentityKey(current);
    if (!ck || visited.has(ck)) break;
    visited.add(ck);
    const parentUuid = (current as { parent_uuid?: string | null }).parent_uuid?.trim();
    const parentId = current.parent_id;
    const pref =
      parentUuid ||
      (parentId != null && parentId !== undefined ? String(parentId) : "");
    if (!pref) break;
    const parent = getComponentFromStructureMap(structureComponents, pref);
    if (!parent) break;
    const pk = getComponentIdentityKey(parent);
    if (pk === hostKey) {
      return current;
    }
    current = parent;
  }
  return null;
}

const EDITOR_ADD_DROPDOWN_INSIDE = "add-inside";
const EDITOR_ADD_DROPDOWN_CHILD_TEMPLATE = "add-child-template";
const EDITOR_ADD_DROPDOWN_SIBLING = "add-sibling";
const EDITOR_ADD_DROPDOWN_PARENT_TEMPLATE = "add-parent-template";

function Editor({
  id: editorSentinelId,
  parent_id,
  parent_uuid,
  keyDownEvent,
}: {
  id: number;
  parent_id: number | null;
  parent_uuid?: string | null;
  keyDownEvent: KeyboardEvent;
}) {
  const selectedStructureKey =
    (parent_uuid && parent_uuid.trim()) ||
    (parent_id != null && parent_id !== undefined ? parent_id : null);

  const component = useAppSelector((state) =>
    selectedStructureKey != null
      ? getComponentFromStructureMap(
          state.builder.structure.components,
          selectedStructureKey
        )
      : undefined
  );

  const editor = useAppSelector((state) => state.builder.editor.editor);
  const editorState = useAppSelector((state) => state.builder.editor);
  const [isLoading, setIsLoading] = useState(false);
  const parentComponent = useAppSelector((state) => {
    const parentKey = getEditorParentComponentMapKey(
      state.builder.editor as EditorState
    );
    return parentKey
      ? getComponentFromStructureMap(state.builder.structure.components, parentKey)
      : undefined;
  });

  /** Actual selected node (matches Builder click + editor state). Sentinel `component` is only the host parent. */
  const builderSelectionComponent = useAppSelector((state) => {
    const componentsMap = state.builder.structure.components;
    const textEditorId = state.textEditor?.get?.("id") ?? state.textEditor?.id;
    const fromTextEditor = getComponentFromStructureMap(
      componentsMap,
      textEditorId
    );
    if (
      fromTextEditor &&
      fromTextEditor.type !== "editor" &&
      fromTextEditor.id !== editorSentinelId
    ) {
      return fromTextEditor;
    }
    const key = resolveEditorComponentStructureKey(
      componentsMap,
      state.builder.editor as EditorState
    );
    if (key) {
      return getComponentFromStructureMap(componentsMap, key);
    }
    return undefined;
  });

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [createComponentTemplate, setCreateComponentTemplate] =
    useState<AllComponents | null>(null);
  const [showConfirmTemplateUnlink, setShowConfirmTemplateUnlink] =
    useState(false);
  const [templateDeleteConflict, setTemplateDeleteConflict] = useState<{
    message: string;
    details: string[];
  } | null>(null);
  const {
    isOpen: showDropdown,
    setIsOpen: setShowDropdown,
    toggle: toggleShowDropdown,
  } = useBuilderExclusiveOverflowMenu();
  const [onMouseUp, setOnMouseUp] = useState<MouseEvent>();
  const website = useAppSelector((state) => state.builder.website);
  const activePageUuid = useAppSelector((state) => state.builder.pages.active);
  const pagesList = useAppSelector((state) => state.builder.pages.list) as Page[];
  const structureNumericId = useMemo(
    () =>
      getLegacyWebpageIdForStructure(
        pagesList,
        activePageUuid as string | null
      ),
    [pagesList, activePageUuid]
  );
  const structure = useAppSelector((state) => state.builder.structure);
  const structureComponentCount = useMemo(
    () => Object.keys(structure.components ?? {}).length,
    [structure.components]
  );
  const templates = useAppSelector((state) => state.builder.templates);

  const [openAddDropdownKey, setOpenAddDropdownKey] = useState<string | null>(
    null
  );

  useEffect(() => {
    return subscribeExclusiveOverflowMenuActive((activeId) => {
      if (
        activeId != null &&
        activeId !== EDITOR_FLOATING_DROPDOWN_OVERFLOW_ID &&
        activeId !== EDITOR_ADD_ELEMENT_MORE_OVERFLOW_ID
      ) {
        setOpenAddDropdownKey(null);
      }
    });
  }, []);

  const makeAddDropdownToggle = useCallback((key: string) => {
    return (next: boolean) => {
      if (next) {
        broadcastExclusiveOverflowMenuActive(
          EDITOR_FLOATING_DROPDOWN_OVERFLOW_ID
        );
        setOpenAddDropdownKey(key);
      } else {
        setOpenAddDropdownKey((k) => (k === key ? null : k));
        if (
          getExclusiveOverflowMenuActiveId() ===
          EDITOR_FLOATING_DROPDOWN_OVERFLOW_ID
        ) {
          broadcastExclusiveOverflowMenuActive(null);
        }
      }
    };
  }, []);

  // If component doesn't exist, don't render the editor
  if (!component) {
    if (editorState.template) {
      return <></>;
    }
    return null;
  }

  const structureActionTarget = builderSelectionComponent ?? component;
  const template = findTemplateForComponent(templates, structureActionTarget);
  const templateId = template ? template.rootComponentUuid : null;

  /** Template overview: duplicating the overlay root targets page structure incorrectly — disable for that node only. */
  const isActiveTemplateRootSelection = Boolean(
    editorState.template &&
      editorTemplateMatchesComponent(
        editorState.template,
        structureActionTarget
      )
  );

  const componentDomId = getComponentDomId(structureActionTarget);

  function unlinkTemplate() {
    duplicateComponent({ bypassTemplateRootGuard: true });
    deleteComponent(structureActionTarget);

    if (template) {
      TemplateAPI.deleteTemplate(template).catch((error: unknown) => {
        if (isTemplateDeleteConflictError(error)) {
          setShowConfirmTemplateUnlink(false);
          setTemplateDeleteConflict(
            parseTemplateDeleteConflictMessage(error)
          );
        }
      });
    }
  }

  const componentType = getComponentType(structureActionTarget);
  const parentComponentType = parentComponent
    ? getComponentType(parentComponent)
    : "";
  const structureComponentsMap = structure.components as Record<
    string,
    AllComponents
  >;

  const dndReorderGate: BuilderDnDReorderGate = useMemo(
    () => ({
      isAllowed: (payload) =>
        isStructureReorderAllowed(structureComponentsMap, payload),
      genericOrderParent: resolveOrderParentKey(parentComponent, component),
      structureOrderParent: resolveStructureParentKeyForOrder(
        parentComponent,
        component.parent_id
      ),
    }),
    [structureComponentsMap, parentComponent, component]
  );

  /** Insert template as child of editor host (e.g. col): after selection among host's children. */
  const childTemplateInsertSortOrder = useMemo(() => {
    if (!component) {
      return sortOrderInsertSiblingAfter(
        structureActionTarget,
        null,
        structureComponentsMap
      );
    }
    const hostKey = getComponentIdentityKey(component);
    const targetKey = getComponentIdentityKey(structureActionTarget);
    if (hostKey && targetKey && hostKey === targetKey) {
      return appendChildSortOrder(component);
    }
    const anchor = getDirectChildUnderHost(
      component,
      structureActionTarget,
      structureComponentsMap
    );
    const sibling = anchor ?? structureActionTarget;
    return sortOrderInsertSiblingAfter(sibling, component, structureComponentsMap);
  }, [component, structureActionTarget, structureComponentsMap]);

  /** Insert template as child of parent section/row: after selection among parent's children. */
  const parentTemplateInsertSortOrder = useMemo(() => {
    if (!parentComponent) {
      return sortOrderInsertSiblingAfter(
        structureActionTarget,
        null,
        structureComponentsMap
      );
    }
    const activeKey = getComponentIdentityKey(structureActionTarget);
    const pKey = getComponentIdentityKey(parentComponent);
    const activeIsParent = !!activeKey && !!pKey && activeKey === pKey;

    if (activeIsParent) {
      return appendChildSortOrder(parentComponent);
    }

    const anchor = getDirectChildUnderHost(
      parentComponent,
      structureActionTarget,
      structureComponentsMap
    );
    const sibling = anchor ?? structureActionTarget;

    return sortOrderInsertSiblingAfter(
      sibling,
      parentComponent,
      structureComponentsMap
    );
  }, [parentComponent, structureActionTarget, structureComponentsMap]);

  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  const [range, saveRange] = useState<Range>();
  const [editorPos, setEditorPos] = useState<number>(28);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShowDropdown(false);
  }, [activeMenu]);

  useEffect(() => {
    /**
     * if clicked on outside of element
     */

    /*************  ✨ Codeium Command ⭐  *************/
    /**
     * Function to handle clicks outside the builder element
     * This function is a hack to save text content when editing text
     * @param event The click event
     */
    /******  a7085627-07ad-414d-8ccc-09f9bc43d682  *******/
    function handleClickOutsideBuilderElement(event: any) {
      const builderElement = inClass("builder-component", ref.current);

      if (builderElement && !builderElement?.contains(event.target)) {
        if (
          ["text", "Button", "nav-item"].includes(component?.type ?? "") &&
          builderElement &&
          builderElement!.querySelector("[contenteditable=true]") &&
          // @ts-ignore
          component.attributes.text !==
            builderElement.querySelector("[contenteditable=true]")!.innerHTML
        ) {
          if (selectedStructureKey != null) {
            updateStructureComponent(
              selectedStructureKey,
              "attributes.text",
              builderElement!.querySelector("[contenteditable=true]")!.innerHTML
            );
          }
        }
      }
    }
    // Bind the event listener
    document.addEventListener("click", handleClickOutsideBuilderElement);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("click", handleClickOutsideBuilderElement);
    };
  }, [ref, selectedStructureKey, component]);


  useEffect(() => {
    const resizeListener = () => {
      setEditorPos(ref.current?.clientHeight ?? 24);
    };
    window.addEventListener("resize", resizeListener);
    return () => window.removeEventListener("resize", resizeListener);
  }, [editor]);

  function triggerResize() {
    setTimeout(() => {
      setEditorPos(ref.current?.clientHeight ?? 24);
    }, 1);
  }

  useEffect(() => {
    function mouseupEvent(e: any) {
      if (
        inClass("builder-component--active", e.target) &&
        !inClass("global-editor__menu", e.target)
      ) {
        setOnMouseUp(e);
        var sel = window.getSelection && window.getSelection();
        if (sel && sel.rangeCount > 0) {
          saveRange(window.getSelection()?.getRangeAt(0));
        }
      }
    }
    window.addEventListener("mouseup", mouseupEvent);

    return () => {
      window.removeEventListener("mouseup", mouseupEvent);
    };
  }, []);

  function setRange(range_ = range) {
    let selection = window.getSelection();
    selection?.removeAllRanges();
    if (range_) {
      selection?.addRange(range_);
    }
  }

  function setMenu(menu: string) {
    if (activeMenu === menu) {
      setActiveMenu(null);
    } else {
      setActiveMenu(menu);
    }
  }

  function deleteComponent(component: AllComponents) {
    setIsLoading(true);
    StructureAPI.deleteComponent(component.webpage_id, component).then((x) => {
      if (!x.hasOwnProperty("message")) setEditableComponentId(null);

      setIsLoading(false);
    });
  }

  function duplicateComponent(options?: { bypassTemplateRootGuard?: boolean }) {
    if (structureNumericId == null) {
      console.error("duplicateComponent: missing structure webpage id");
      return;
    }

    if (
      !options?.bypassTemplateRootGuard &&
      isActiveTemplateRootSelection
    ) {
      return;
    }

    setIsLoading(true);

    StructureAPI.duplicateComponent(structureNumericId, structureActionTarget)
      .then((duplicatedComponents) => {
        const duplicatedMap =
          (duplicatedComponents as any)?.payload ?? duplicatedComponents;
        const rootClone =
          (duplicatedComponents as any)?.duplicatedRoot ??
          duplicatedMap[Object.keys(duplicatedMap)[0]];

        if (!rootClone) {
          console.error("No component returned from duplicate operation");
          setIsLoading(false);
          return;
        }

        setIsLoading(false);

        deleteComponentById(editorSentinelId);
        addNewComponent({
          id: editorSentinelId,
          type: "editor",
          sort_order: -1,
          parent_id: rootClone.id,
          childElements: [],
        } as any);
        setEditableComponentId(getComponentIdentityKey(rootClone));
      })
      .catch((error) => {
        console.error("Error duplicating component:", error);
        setIsLoading(false);
      });
  }


  useLayoutEffect(() => {
    if (activeMenu) {
      triggerResize();
    }
  }, [activeMenu, triggerResize]);

  useEffect(() => {
    // Scope lookup to this editor instance to avoid binding a different
    // toolbar handle when both page and template editors exist in DOM.
    const editorRoot = ref.current?.parentElement as HTMLElement | null;
    const editorDraghandle = editorRoot?.querySelector(".editorDraghandle");

    // Needed variable for contentComponent drag and drop -> needs refactor
    let isText = false;

    const onMouseDown = (e) => {
      handleMouseDown(e, ref, isText);

    };

    const onMouseUp = (e) => {
      handleMouseUp(e, ref, isText);
    };

    const onDragStart = (e) => {
      handleDragStart(e, ref, isText);

    };

    const onDragEnd = (e) => {
      handleDragEnd(e, ref);
    };

    const handleDragOverPosition = (e) => {
      e.preventDefault();
      handleDragOverPos(e, ref, isText, dndReorderGate);
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).addEventListener(
        "dragover",
        handleDragOverPosition
      );
    };

    const handleDragLeave = (e) => {
      handleDragLeavePos(e, ref, handleDragOverPosition);
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = "move";
      return false;
    };

    const ondrop = (e) => {
      const builderElement = inClass("builder-component--active", ref.current);
      const targetElement = inClass("builder-component", e.target) as HTMLElement | null;

      e.preventDefault();
      e.stopPropagation();

      if (!targetElement) {
        return;
      }
      const targetParent = targetElement.offsetParent as HTMLElement | null;
      const targetParentParent = targetParent?.offsetParent as HTMLElement | null;

      // Remove the styles that were left on the target
      targetElement.style.webkitBoxShadow = "";
      targetElement.style.boxShadow = "";
      targetElement.style.setProperty("-moz-box-shadow", "");

      if (targetParent && targetParent.style) {
        targetParent.style.webkitBoxShadow = "";
        targetParent.style.boxShadow = "";
        targetParent.style.setProperty("-moz-box-shadow", "");
      }

      if (targetElement.firstChild && (targetElement.firstChild as HTMLElement).style) {
        (targetElement.firstChild as HTMLElement).style.webkitBoxShadow = "";
        (targetElement.firstChild as HTMLElement).style.boxShadow = "";
        (targetElement.firstChild as HTMLElement).style.setProperty(
          "-moz-box-shadow",
          ""
        );
        (targetElement.firstChild as HTMLElement).style.transition = `none`;
      }

      if (!builderElement) {
        return;
      }

      if (builderElement.getAttribute("data-component-type") === "section") {
        if (builderElement.getAttribute("data-drag-type") === "main") {
          if (
            targetElement.getAttribute("data-component-type") === "section" &&
            targetParent &&
            !targetParent.classList.contains("builder-component")
          ) {
            const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
            const destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
            const dropLocation = builderElement.getAttribute("data-position");

            updateStructureWebpageComponentsOrder(
              draggedComponentId,
              destinationId,
              dropLocation
            );
          }
        } else if (
          builderElement.getAttribute("data-drag-type") !== "main" &&
          builderElement.getAttribute("data-drag-type") !== "inner-section"
        ) {
          const columnDropLayoutParent = resolveColumnReorderLayoutParent(
            targetElement.offsetParent as HTMLElement | null
          );
          if (
            targetElement.getAttribute("data-component-type") === "section" &&
            columnDropLayoutParent != null &&
            isColumnReorderLayoutParent(columnDropLayoutParent) &&
            builderElement.id !== targetElement.id &&
            columnDropLayoutParent.offsetParent?.id !== builderElement.id
          ) {
            const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
            const destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
            const dropLocation = builderElement.getAttribute("data-position");

            if (
              shouldDispatchStructureReorder(structureComponentsMap, {
                parent_id: resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,
                position: dropLocation,
                section: true,
              })
            ) {
              updateComponentStructureOrder(
                resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,

                dropLocation,
                true
              );
              deleteComponentById(editorSentinelId);
              setEditableComponentId(null);
            }
          } else if (
            targetElement.getAttribute("data-component-type") === "section" &&
            targetElement.classList.contains("builder-component") &&
            targetElement.classList.contains("row") &&
            !targetParent?.classList.contains("builder-component") &&
            !builderElement.offsetParent?.classList.contains("col") &&
            builderElement?.offsetParent?.id !== targetElement?.id &&
            builderElement.id !== targetElement.id
          ) {
            const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
            const destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
            const dropLocation = builderElement.getAttribute("data-position");

            if (
              shouldDispatchStructureReorder(structureComponentsMap, {
                parent_id: resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,
                position: dropLocation,
                section: true,
              })
            ) {
              updateComponentStructureOrder(
                resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,

                dropLocation,
                true
              );

              deleteComponentById(editorSentinelId);
              setEditableComponentId(null);
            }
          } else if (
            targetElement.getAttribute("data-component-type") === "inner-section" &&
            targetElement.classList.contains("builder-component") &&
            targetParent?.classList.contains("col") &&
            builderElement?.offsetParent?.id !== targetElement.id &&
            builderElement.id !== targetElement.id
          ) {
            const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
            const destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
            const dropLocation = builderElement.getAttribute("data-position");

            if (
              shouldDispatchStructureReorder(structureComponentsMap, {
                parent_id: resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,
                position: dropLocation,
                section: true,
              })
            ) {
              updateComponentStructureOrder(
                resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,
                dropLocation,
                true
              );
            }
          } else if (
            targetElement.getAttribute("data-component-type") === "footer" &&
            targetElement.classList.contains("builder-component") &&
            !targetParent?.classList.contains("builder-component") &&
            !builderElement.offsetParent?.classList.contains("col") &&
            !targetParent?.classList.contains("inner-section") &&
            builderElement?.offsetParent?.id !== targetElement.id &&
            builderElement.id !== targetElement.id &&
            builderElement.getAttribute("data-component-type") !==
              "inner-section"
          ) {
            const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
            const destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
            const dropLocation = builderElement.getAttribute("data-position");

            if (
              shouldDispatchStructureReorder(structureComponentsMap, {
                parent_id: resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,
                position: dropLocation,
                section: true,
              })
            ) {
              updateComponentStructureOrder(
                resolveStructureParentKeyForOrder(
                  parentComponent,
                  component.parent_id
                ),
                draggedComponentId,
                destinationId,

                dropLocation,
                true
              );

              deleteComponentById(editorSentinelId);
              setEditableComponentId(null);
            }
          }
        }
      }

      if (
        builderElement.getAttribute("data-component-type") === "inner-section"
      ) {
        if (
          targetElement.getAttribute("data-component-type") === "section" &&
          targetParent?.classList.contains("builder-component") &&
          !targetParent?.classList.contains("inner-section") &&
          targetElement !== builderElement.offsetParent
        ) {
          const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
          let destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
          const dropLocation = builderElement.getAttribute("data-position");

          if (!destinationId) {
            destinationId = targetElement.id;
          }

          if (
            shouldDispatchStructureReorder(structureComponentsMap, {
              parent_id: resolveStructureParentKeyForOrder(
                parentComponent,
                component.parent_id
              ),
              draggedComponentId,
              destinationId,
              position: dropLocation,
              section: false,
            })
          ) {
            updateComponentStructureOrder(
              resolveStructureParentKeyForOrder(
                parentComponent,
                component.parent_id
              ),
              draggedComponentId,
              destinationId,
              dropLocation,
              false
            );
            deleteComponentById(editorSentinelId);
            setEditableComponentId(null);
          }
        } else if (
          targetParent?.classList.contains("builder-component") &&
          !targetParent?.classList.contains("inner-section") &&
          targetParent?.classList.contains("col") &&
          !(targetParentParent?.classList.contains("inner-section") ?? false)
        ) {
          const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
          let destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
          const dropLocation = builderElement.getAttribute("data-position");

          if (!destinationId) {
            destinationId = targetElement.id;
          }

          if (
            shouldDispatchStructureReorder(structureComponentsMap, {
              parent_id: resolveOrderParentKey(parentComponent, component),
              draggedComponentId,
              destinationId,
              position: dropLocation,
              section: false,
            })
          ) {
            updateComponentStructureOrder(
              resolveOrderParentKey(parentComponent, component),
              draggedComponentId,
              destinationId,
              dropLocation,
              false
            );
            deleteComponentById(editorSentinelId);
            setEditableComponentId(null);
          }
        }

      }

      if (
        builderElement.getAttribute("data-component-type") !== "section" &&
        builderElement.getAttribute("data-component-type") !== "inner-section"
      ) {
        if (
          targetElement.getAttribute("data-component-type") === "section" &&
          targetParent?.classList.contains("builder-component")
        ) {
          const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
          let destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
          const dropLocation = builderElement.getAttribute("data-position");

          if (!destinationId) {
            destinationId = targetElement.id;
          }

          if (
            shouldDispatchStructureReorder(structureComponentsMap, {
              parent_id: resolveOrderParentKey(parentComponent, component),
              draggedComponentId,
              destinationId,
              position: dropLocation,
              section: false,
            })
          ) {
            updateComponentStructureOrder(
              resolveOrderParentKey(parentComponent, component),

              draggedComponentId,
              destinationId,
              dropLocation,
              false
            );
            deleteComponentById(editorSentinelId);
            setEditableComponentId(null);
          }
        } else if (
          targetElement.getAttribute("data-component-type") !== "section" &&
          targetParent?.classList.contains("builder-component") &&
          !targetElement.classList.contains("global-editor") &&
          targetParent.id !== builderElement.id &&
          targetElement.id !== builderElement.id
        ) {
          const draggedComponentId = builderElement.getAttribute("id"); // ID of the element that was dragged
          let destinationId = targetElement.getAttribute("id"); // ID of the element, which we dragged on
          const dropLocation = builderElement.getAttribute("data-position");

          if (!destinationId) {
            destinationId = targetElement.id;
          }

          if (
            shouldDispatchStructureReorder(structureComponentsMap, {
              parent_id: resolveOrderParentKey(parentComponent, component),
              draggedComponentId,
              destinationId,
              position: dropLocation,
              section: false,
            })
          ) {
            updateComponentStructureOrder(
              resolveOrderParentKey(parentComponent, component),
              draggedComponentId,
              destinationId,
              dropLocation,
              false
            );
            deleteComponentById(editorSentinelId);
            setEditableComponentId(null);
          }
        }
      }
    };

    const ondragover = (e) => {
      e.preventDefault();
    };

    const builderElement = inClass("builder-component--active", ref.current);
    // Editor Event Listeners
    editorDraghandle?.addEventListener("mousedown", onMouseDown);
    editorDraghandle?.addEventListener("mouseup", onMouseUp);

    // BuilderElement Event Listeners
    builderElement?.addEventListener("dragstart", onDragStart);
    builderElement?.addEventListener("dragend", onDragEnd);

    // Define drop targets events
    let dropTargets = Array.from(
      document.querySelectorAll(".builder-component")
    );
    dropTargets.forEach((dropTarget) => {
      dropTarget.addEventListener("drop", ondrop);
      dropTarget.addEventListener("dragenter", handleDragEnter);
      dropTarget.addEventListener("dragleave", handleDragLeave);
      dropTarget.addEventListener("dragover", handleDragOver);
    });

    return () => {
      // Unbind the event listeners on cleanup
      document.removeEventListener("dragover", ondragover);
      editorDraghandle?.removeEventListener("mousedown", onMouseDown);
      editorDraghandle?.removeEventListener("mouseup", onMouseUp);
      builderElement?.removeEventListener("dragend", onDragEnd);
      builderElement?.removeEventListener("dragstart", onDragStart);

      // Remove drop targets event listeners
      dropTargets.forEach((dropTarget) => {
        dropTarget.removeEventListener("drop", ondrop);
        dropTarget.removeEventListener("dragenter", handleDragEnter);
        dropTarget.removeEventListener("dragleave", handleDragLeave);
        dropTarget.removeEventListener("dragover", handleDragOver);
      });
    };
  }, [
    componentDomId,
    structureActionTarget?.parent_id,
    (structureActionTarget as { parent_uuid?: string | null } | undefined)
      ?.parent_uuid,
    structureComponentCount,
    // Re-query drop targets when the root section list changes so freshly
    // inserted template components get drop/dragover listeners immediately.
    structure.webpageComponents,
    structureComponentsMap,
    parentComponent,
    component,
    dndReorderGate,
  ]);

  const isTemplateOverlayEdit = Boolean(editorState.template);
  const tooltip = document.getElementById("tooltip");
  const element = getBuilderComponentRootElement(structureActionTarget);

  const positionEditorTooltip = useCallback(() => {
    const tooltipEl = document.getElementById("tooltip");
    const reference = getBuilderComponentRootElement(structureActionTarget);
    const sidebar = document.getElementsByClassName("sidebar");

    let leftValue = 0;
    if (reference?.classList.contains("row")) {
      leftValue = sidebar.length >= 1 ? 0 : -40;
    }

    if (!reference || !tooltipEl) {
      return;
    }

    computePosition(reference, tooltipEl, {
      placement: "top-start",
      strategy: isTemplateOverlayEdit ? "fixed" : "absolute",
      middleware: [flip(), shift({ padding: isTemplateOverlayEdit ? 8 : 0 })],
    }).then(({ x, y }) => {
      Object.assign(tooltipEl.style, {
        left: `${sidebar.length >= 1 ? x + leftValue : x}px`,
        top: `${y}px`,
        position: isTemplateOverlayEdit ? "fixed" : "absolute",
        zIndex: isTemplateOverlayEdit ? "10001" : "9998",
      });
    });
  }, [isTemplateOverlayEdit, structureActionTarget]);

  useLayoutEffect(() => {
    positionEditorTooltip();
  }, [element, tooltip, editorPos, activeMenu, positionEditorTooltip]);

  useEffect(() => {
    if (!isTemplateOverlayEdit) {
      return;
    }

    const scrollEl = document.getElementById("template_edit_over_lay_content");
    if (!scrollEl) {
      return;
    }

    scrollEl.addEventListener("scroll", positionEditorTooltip, { passive: true });
    return () => {
      scrollEl.removeEventListener("scroll", positionEditorTooltip);
    };
  }, [isTemplateOverlayEdit, positionEditorTooltip]);

  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  // Handle manual repositioning on open
  // Adjusted onToggle handler type
  const handleToggle = (isOpen: boolean, event?: Event | SyntheticEvent) => {
    if (isOpen && dropdownMenuRef.current) {
      positionDropdown();
    }
  };

  // Reposition dropdown function
  const positionDropdown = () => {
    if (dropdownMenuRef.current) {
      const dropdown = dropdownMenuRef.current;
      const rect = dropdown.getBoundingClientRect();
      if (rect.top < 0) {
        dropdown.style.top = `${-rect.top + 10}px`; // Adjusting for overflow above
      }
      if (rect.left < 0) {
        dropdown.style.left = `${-rect.left + 10}px`; // Adjusting for overflow to the left
      }
    }
  };

  // Adjust dropdown position on window resize
  useEffect(() => {
    const handleResize = () => {
      positionDropdown();
      positionEditorTooltip();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [positionEditorTooltip]);
  const editorTooltip = (
      <div
        id="tooltip"
        role="tooltip"
        className={isTemplateOverlayEdit ? "template-editor-tooltip" : undefined}
        style={{ zIndex: isTemplateOverlayEdit ? 10001 : 9998, width: "auto!important" }}
      >
        {editor && selectedStructureKey != null && (
          <>
            <TextEditorComponent id={selectedStructureKey} />
          </>
        )}

        {[
          "header",
          "logo",
          "nav",
          "cart",
          "language-select",
          "section",
        ].includes(componentType) && (
          <div style={{ marginTop: editorPos + "px" }}></div>
        )}
        <div
          id={editorSentinelId ? editorSentinelId.toString() : ""}
          ref={ref}
          className={"global-editor global-editor__menu"}
          style={{
            top: [
              "row",
              "logo",
              "nav",
              "cart",
              "header",
              "section",
              "nav-item",
              "language-select",
            ].includes(componentType)
              ? "0px"
              : "-" + editorPos + "px",
            width: "auto",
          }}
        ></div>

        <div className="global-editor global-editor__top-menu">
          <div className="col-12 d-flex flex-nowrap">
            {!["header", "logo", "nav", "footer"].includes(componentType) &&
              parentComponentType !== "Accordion" && (
                <div
                  className="global-editor global-editor__top-menu__item editorDraghandle"
                  style={{ cursor: "grab" }}
                >
                  <SwapVertIcon />
                </div>
              )}

            <SelectEditorMenu
              componentType={componentType}
              activeMenu={activeMenu}
              setMenu={setMenu}
            />

            {(componentType === "row" || componentType === "inner_section") &&
              !inClass(
                "builder-footer",
                getBuilderComponentRootElement(structureActionTarget)!
              ) && (
                <div
                  className={
                    "global-editor global-editor__top-menu__item" +
                    (activeMenu === "contentWidthMenu"
                      ? " global-editor__top-menu__item--active"
                      : "")
                  }
                  onClick={() => {
                    setShowDropdown(false);
                    setMenu("contentWidthMenu");
                  }}
                >
                  <WidthWideOutlinedIcon />
                </div>
              )}

            {parentComponentType === "footer" && (
              <div
                className={
                  "global-editor global-editor__top-menu__item" +
                  (activeMenu === "contentWidthMenu"
                    ? " global-editor__top-menu__item--active"
                    : "")
                }
                onClick={() => {
                  setShowDropdown(false);
                  setMenu("contentWidthMenu");
                }}
              >
                <WidthWideOutlinedIcon />
              </div>
            )}

            {template && (
              <div
                className={"global-editor global-editor__top-menu__item"}
                onClick={() => {
                  setShowDropdown(false);
                  setCreateComponentTemplate(structureActionTarget);
                }}
              >
                <DashboardIcon />
              </div>
            )}

            {!["logo", "nav"].includes(componentType) && (
              <div className="global-editor global-editor__top-menu__item flex-grow-1 text-end position-relative">
                <MoreHorizOutlined
                  data-bs-toggle="dropdown"
                  onClick={() => {
                    toggleShowDropdown();
                  }}
                />
                <ul
                  style={{ top: "30px", right: "0px" }}
                  className={
                    "dropdown-menu b-dropdown-menu dropdown-menu-end builder-dropdown " +
                    (showDropdown ? "show" : "")
                  }
                >
                  {(!["row", "header"].includes(componentType) ||
                    (componentType === "row" &&
                      !inClass(
                        "builder-footer",
                        getBuilderComponentRootElement(structureActionTarget)!
                      ))) &&
                    !isActiveTemplateRootSelection && (
                    <li>
                      <span
                        style={{
                          cursor: isLoading ? "not-allowed" : "pointer",
                        }}
                        className="dropdown-item builder-dropdown__item"
                        onClick={() => {
                          if (isLoading) {
                            return;
                          }

                          duplicateComponent();
                        }}
                      >
                        Create a copy
                        {isLoading && (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                            style={{ marginLeft: "5px" }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        )}
                      </span>
                    </li>
                  )}

                  {(!["row", "header", "footer"].includes(componentType) ||
                    (componentType === "row" &&
                      !inClass(
                        "builder-footer",
                        getBuilderComponentRootElement(structureActionTarget)!
                      ))) && (
                    <>
                      {!templateId ? (
                        <li>
                          <span
                            style={{
                              cursor: isLoading ? "not-allowed" : "pointer",
                            }}
                            className="dropdown-item builder-dropdown__item"
                            onClick={() => {
                              if (isLoading) {
                                return;
                              }

                              setShowDropdown(false);
                              setCreateComponentTemplate(structureActionTarget);
                            }}
                          >
                            Save template
                          </span>
                        </li>
                      ) : (
                        <li>
                          <span
                            style={{
                              cursor: isLoading ? "not-allowed" : "pointer",
                            }}
                            className="dropdown-item builder-dropdown__item"
                            onClick={() => {
                              if (isLoading) {
                                return;
                              }

                              setShowConfirmTemplateUnlink(true);
                            }}
                          >
                            Unlink template
                          </span>
                        </li>
                      )}
                    </>
                  )}
                  <li>
                    <span
                      style={{
                        cursor: isLoading ? "not-allowed" : "pointer",
                      }}
                      className="dropdown-item builder-dropdown__item"
                      onClick={() => {
                        if (isLoading) {
                          return;
                        }
                        setShowConfirmation(true);
                      }}
                    >
                      Remove
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        {activeMenu && selectedStructureKey != null && (
          <div className="global-editor global-editor__bottom-menu b-p-1">
            <BottomMenu
              id={selectedStructureKey}
              activeMenu={activeMenu}
              triggerResize={triggerResize}
              onMouseUp={onMouseUp}
              setRange={setRange}
              keyDownEvent={keyDownEvent}
            />
          </div>
        )}
        {showConfirmation && (
          <Confirmation
            id={componentDomId}
            onCancel={() => {
              setShowConfirmation(false);
            }}
            onConfirm={() => {
              // if (componentType === "row" && inClass("builder-footer", document.getElementById(id))) {
              //   deleteComponent(parseInt(document.getElementsByClassName("builder-footer")[0].id))
              // } else {
              deleteComponent(structureActionTarget);
              // }
            }}
            text="Are you sure you want to remove this element?"
            isLoading={isLoading}
          />
        )}
        {showConfirmTemplateUnlink && (
          <Confirmation
            id={componentDomId}
            onCancel={() => {
              setShowConfirmTemplateUnlink(false);
            }}
            onConfirm={() => {
              unlinkTemplate();
            }}
            confirmText="Unlink"
            text="Are you sure you want to unlink this element from template? After nulinking, changing this element will not change the template."
          />
        )}
        {createComponentTemplate && (
          <>
            <CreateTemplateModal
              createComponentTemplate={createComponentTemplate}
              template={template}
              setCreateComponentTemplate={setCreateComponentTemplate}
            />
          </>
        )}
        <BuilderAlertModal
          open={templateDeleteConflict != null}
          message={templateDeleteConflict?.message ?? ""}
          details={templateDeleteConflict?.details}
          onClose={() => setTemplateDeleteConflict(null)}
        />
      </div>
  );

  return (
      <>
      {isTemplateOverlayEdit
        ? createPortal(editorTooltip, document.body)
        : editorTooltip}

      <div className="add-new-element-buttons">
        {/* Inside current selection: children, column-in-row, child templates */}
        <div className="p-0">
          {(componentType === "col" ||
            componentType === "nav" ||
            componentType === "navigation-container") && (
            <Dropdown
              drop="up"
              show={openAddDropdownKey === EDITOR_ADD_DROPDOWN_INSIDE}
              onToggle={makeAddDropdownToggle(EDITOR_ADD_DROPDOWN_INSIDE)}
            >
              <Dropdown.Toggle>
                {(props) => (
                  <span
                    {...props}
                    className="add-new-element-button elementB inline-block"
                  >
                    <AddIcon />
                  </span>
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu offset={[0, -19]}>
                {(props, { show }) =>
                  createPortal(
                    <div {...props} className="builder-dropdown">
                      <SelectElementType component={component} />
                    </div>,
                    document.body
                  )
                }
              </Dropdown.Menu>
            </Dropdown>
          )}
          {(componentType === "row" ||
            componentType === "inner_section" ||
            componentType === "footer" ||
            componentType === "Accordion") && (
            <div
              className="add-new-element-button colB"
              style={{ display: "inline-block" }}
              onClick={() =>
                StructureAPI.addComponent(structureActionTarget.webpage_id, {
                  type:
                    componentType === "inner_section" ||
                    componentType === "footer" ||
                    componentType === "Accordion"
                      ? "section"
                      : structureActionTarget.type,
                  sort_order: appendChildSortOrder(structureActionTarget),
                  attributes: [
                    {
                      name: "classes",
                      value: DEFAULT_NEW_COLUMN_CLASSES,
                      type: "attributes",
                    },
                    { name: "paddingTop", value: "20px", type: "styles" },
                    { name: "paddingLeft", value: "10px", type: "styles" },
                    { name: "paddingBottom", value: "20px", type: "styles" },
                    { name: "paddingRight", value: "10px", type: "styles" },
                  ],
                  parent_id: structureActionTarget.id ?? null,
                  parent_uuid: structureActionTarget.uuid?.trim()
                    ? structureActionTarget.uuid
                    : null,
                }).then((x) => {
                  const nextEditorComponent = Object.values(
                    (x as any)?.payload ?? {}
                  )[0] as AllComponents | undefined;
                  const nextEditorTarget = nextEditorComponent
                    ? getComponentIdentityKey(nextEditorComponent)
                    : null;
                  deleteComponentById(editorSentinelId);
                  addNewComponent({
                    id: editorSentinelId,
                    type: "editor",
                    sort_order: 1,
                    parent_id: nextEditorComponent?.id ?? null,
                    ...(nextEditorComponent?.uuid?.trim()
                      ? { parent_uuid: nextEditorComponent.uuid.trim() }
                      : {}),
                    childElements: [],
                  } as any);
                  setEditableComponentId(nextEditorTarget);
                })
              }
            >
              <AddIcon />
            </div>
          )}
          {[
            "col",
            "row",
            "inner_section",
            "Accordion",
            "header",
            "footer",
          ].includes(componentType) && (
            <Dropdown
              drop="up"
              show={openAddDropdownKey === EDITOR_ADD_DROPDOWN_CHILD_TEMPLATE}
              onToggle={makeAddDropdownToggle(
                EDITOR_ADD_DROPDOWN_CHILD_TEMPLATE
              )}
            >
              <Dropdown.Toggle>
                {(props) => (
                  <span
                    {...props}
                    className={
                      "inline-block add-new-element-button " +
                      (componentType === "col" ? "elementB" : "colB")
                    }
                  >
                    <LibraryAddIcon />
                  </span>
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu offset={[0, -19]}>
                {(props, { show }) =>
                  createPortal(
                    <div {...props} className="builder-dropdown">
                      <SelectTemplate
                        key={"childTemplate"}
                        component={component}
                        sort_order={childTemplateInsertSortOrder}
                      />
                    </div>,
                    document.body
                  )
                }
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
        {/* Outside current selection: siblings, parent-scoped templates, duplicate */}
        <div className="p-0">
          {["col", "row", "inner_section"].includes(componentType) && (
            <div
              style={{ display: "inline-block" }}
              className={
                "add-new-element-button " +
                (componentType === "col" ? "colB" : "rowB")
              }
              onClick={() => {
                if (componentType === "col") {
                  StructureAPI.addComponent(structureActionTarget.webpage_id, {
                    type: structureActionTarget.type,
                    sort_order: sortOrderInsertSiblingAfter(
                      structureActionTarget,
                      parentComponent,
                      structure.components as Record<string, AllComponents>
                    ),
                    attributes: [
                      {
                        name: "classes",
                        value:
                          DEFAULT_NEW_COLUMN_CLASSES,
                        type: "attributes",
                      },
                      { name: "paddingTop", value: "20px", type: "styles" },
                      { name: "paddingLeft", value: "10px", type: "styles" },
                      { name: "paddingBottom", value: "20px", type: "styles" },
                      { name: "paddingRight", value: "10px", type: "styles" },
                    ],
                    ...siblingAddParentPayload(
                      structureActionTarget,
                      parentComponent
                    ),
                  }).then((x) => {
                    const nextEditorComponent = Object.values(
                      (x as any)?.payload ?? {}
                    )[0] as AllComponents | undefined;
                    const nextEditorTarget = nextEditorComponent
                      ? getComponentIdentityKey(nextEditorComponent)
                      : null;
                    deleteComponentById(editorSentinelId);
                    addNewComponent({
                      id: editorSentinelId,
                      type: "editor",
                      sort_order: 1,
                      parent_id: nextEditorComponent?.id ?? null,
                      ...(nextEditorComponent?.uuid?.trim()
                        ? { parent_uuid: nextEditorComponent.uuid.trim() }
                        : {}),
                      childElements: [],
                    } as any);
                    setEditableComponentId(nextEditorTarget);
                  });
                } else if (componentType === "inner_section") {
                  StructureAPI.addComponent(structureActionTarget.webpage_id, {
                    type: "inner_section",
                    sort_order: sortOrderInsertSiblingAfter(
                      structureActionTarget,
                      parentComponent,
                      structure.components as Record<string, AllComponents>
                    ),
                    attributes: [
                      {
                        name: "classes",
                        value: "inner-section",
                        type: "attributes",
                      },
                      { name: "paddingTop", value: "20px", type: "styles" },
                      { name: "paddingLeft", value: "0px", type: "styles" },
                      { name: "paddingBottom", value: "20px", type: "styles" },
                      { name: "paddingRight", value: "0px", type: "styles" },
                      {
                        name: "content_width_unit",
                        value: website?.contentWidthUnit,
                        type: "attributes",
                      },
                      {
                        name: "content_width_size",
                        value: website?.contentWidthSize,
                        type: "attributes",
                      },
                    ],
                    ...siblingAddParentPayload(
                      structureActionTarget,
                      parentComponent
                    ),
                    childElements: [
                      {
                        type: "section",
                        sort_order: 1,
                        attributes: [
                          {
                            name: "classes",
                            value: DEFAULT_NEW_COLUMN_CLASSES,
                            type: "attributes",
                          },
                          { name: "paddingTop", value: "20px", type: "styles" },
                          {
                            name: "paddingLeft",
                            value: "20px",
                            type: "styles",
                          },
                          {
                            name: "paddingBottom",
                            value: "20px",
                            type: "styles",
                          },
                          {
                            name: "paddingRight",
                            value: "20px",
                            type: "styles",
                          },
                        ],
                      },
                    ],
                  }).then((x) => {
                    const nextEditorComponent = Object.values(
                      (x as any)?.payload ?? {}
                    )[0] as AllComponents | undefined;
                    const nextEditorTarget = nextEditorComponent
                      ? getComponentIdentityKey(nextEditorComponent)
                      : null;
                    updateStructureWebpageComponentsOrder(
                      nextEditorTarget,
                      getComponentIdentityKey(structureActionTarget),
                      "bottom"
                    );

                    deleteComponentById(editorSentinelId);
                    addNewComponent({
                      id: editorSentinelId,
                      type: "editor",
                      sort_order: 1,
                      parent_id: nextEditorComponent?.id ?? null,
                      ...(nextEditorComponent?.uuid?.trim()
                        ? { parent_uuid: nextEditorComponent.uuid.trim() }
                        : {}),
                      childElements: [],
                    } as any);
                    setEditableComponentId(nextEditorTarget);
                  });
                } else if (
                  componentType === "row" ||
                  componentType === "footer"
                ) {
                  StructureAPI.addComponent(structureActionTarget.webpage_id, {
                    type: structureActionTarget.type,
                    sort_order: sortOrderInsertSiblingAfter(
                      structureActionTarget,
                      parentComponent,
                      structure.components as Record<string, AllComponents>
                    ),
                    attributes: [
                      { name: "classes", value: "row", type: "attributes" },
                      { name: "paddingTop", value: "20px", type: "styles" },
                      { name: "paddingLeft", value: "10px", type: "styles" },
                      { name: "paddingBottom", value: "20px", type: "styles" },
                      { name: "paddingRight", value: "10px", type: "styles" },
                      {
                        name: "content_width_unit",
                        value: website?.contentWidthUnit,
                        type: "attributes",
                      },
                      {
                        name: "content_width_size",
                        value: website?.contentWidthSize,
                        type: "attributes",
                      },
                    ],
                    ...siblingAddParentPayload(
                      structureActionTarget,
                      parentComponent
                    ),
                    childElements: [
                      {
                        type: "section",
                        sort_order: 1,
                        attributes: [
                          {
                            name: "classes",
                            value:
                              DEFAULT_NEW_COLUMN_CLASSES,
                            type: "attributes",
                          },
                          { name: "paddingTop", value: "20px", type: "styles" },
                          {
                            name: "paddingLeft",
                            value: "10px",
                            type: "styles",
                          },
                          {
                            name: "paddingBottom",
                            value: "20px",
                            type: "styles",
                          },
                          {
                            name: "paddingRight",
                            value: "10px",
                            type: "styles",
                          },
                        ],
                      },
                    ],
                  }).then((x) => {
                    const nextEditorComponent = Object.values(
                      (x as any)?.payload ?? {}
                    )[0] as AllComponents | undefined;
                    const nextEditorTarget = nextEditorComponent
                      ? getComponentIdentityKey(nextEditorComponent)
                      : null;
                    updateStructureWebpageComponentsOrder(
                      nextEditorTarget,
                      getComponentIdentityKey(structureActionTarget),
                      "bottom"
                    );

                    deleteComponentById(editorSentinelId);
                    addNewComponent({
                      id: editorSentinelId,
                      type: "editor",
                      sort_order: 1,
                      parent_id: nextEditorComponent?.id ?? null,
                      ...(nextEditorComponent?.uuid?.trim()
                        ? { parent_uuid: nextEditorComponent.uuid.trim() }
                        : {}),
                      childElements: [],
                    } as any);
                    setEditableComponentId(nextEditorTarget);
                  });
                }
              }}
            >
              <AddIcon />
            </div>
          )}
          {![
            "row",
            "col",
            "inner_section",
            "logo",
            "nav",
            "cart",
            "navigation-container",
            "language-select",
          ].includes(componentType) &&
            parentComponent &&
            ["col", "nav", "navigation-container", "inner_section", "Accordion"].includes(getComponentType(parentComponent)) && (
              <Dropdown
                drop="up"
                show={openAddDropdownKey === EDITOR_ADD_DROPDOWN_SIBLING}
                onToggle={makeAddDropdownToggle(EDITOR_ADD_DROPDOWN_SIBLING)}
              >
                <Dropdown.Toggle>
                  {(props) => (
                    <span
                      {...props}
                      className="add-new-element-button elementB inline-block"
                    >
                      <AddIcon />
                    </span>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu offset={[0, -19]}>
                  {(props, { show }) =>
                    createPortal(
                      <div {...props} className="builder-dropdown">
                        <SelectElementType component={structureActionTarget} />
                      </div>,
                      document.body
                    )
                  }
                </Dropdown.Menu>
              </Dropdown>
            )}

          {![
            "header",
            "logo",
            "nav",
            "cart",
            "language-select",
            "nav-item",
          ].includes(componentType) && (
            <Dropdown
              drop="up"
              show={openAddDropdownKey === EDITOR_ADD_DROPDOWN_PARENT_TEMPLATE}
              onToggle={makeAddDropdownToggle(
                EDITOR_ADD_DROPDOWN_PARENT_TEMPLATE
              )}
            >
              <Dropdown.Toggle>
                {(props: any) => (
                  <span
                    {...props}
                    className={
                      "inline-block add-new-element-button " +
                      (componentType === "col"
                        ? "colB"
                        : componentType === "row"
                        ? "rowB"
                        : "elementB")
                    }
                  >
                    <LibraryAddIcon />
                  </span>
                )}
              </Dropdown.Toggle>
              <Dropdown.Menu offset={[0, -19]}>
                {(props: any, { show: any }) =>
                  createPortal(
                    <div {...props} className="builder-dropdown">
                      <SelectTemplate
                        key={"parentTemplate"}
                        component={parentComponent}
                        sort_order={parentTemplateInsertSortOrder}
                        insertAfterWebpageComponentRef={
                          parentComponent == null
                            ? getComponentIdentityKey(structureActionTarget) ??
                              undefined
                            : undefined
                        }
                      />
                    </div>,
                    document.body
                  )
                }
              </Dropdown.Menu>
            </Dropdown>
          )}

          {![
            "header",
            "footer",
            "logo",
            "nav",
            "cart",

            "language-select",
          ].includes(componentType) &&
            !isActiveTemplateRootSelection && (
            <div
              style={{
                display: "inline-block",
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
              className={
                "add-new-element-button " +
                (componentType === "col"
                  ? "colB"
                  : componentType === "row"
                  ? "rowB"
                  : "elementB")
              }
              onClick={() => {
                if (isLoading) {
                  return;
                }

                duplicateComponent();
              }}
            >
              {isLoading ? <Spinner size="sm" as="div" /> : <ContentCopyIcon />}
            </div>
          )}
        </div>
      </div>
      </>
  );
}

export default memo(Editor);
